// Splits the printed invoice into A4 sheets from measured heights (CSS pixels). Pure, so the rules
// are testable without a browser:
// - a block (parties, totals, signatures…) is never split; it moves to the next sheet when it does
//   not fit;
// - a row section (invoice rows, payments, description lines) is split between rows, repeats its
//   head on every sheet, and reserves its foot (the page and running totals of the rows) on every
//   sheet it appears on;
// - the last rows stay with the block that follows them (the totals), so no sheet starts with the
//   totals alone while rows could have come along;
// - a block with a compact form (the signatures) takes it rather than a sheet of its own.

export type FlowSection =
  | {
      kind: 'block'
      key: string
      height: number
      /** Height of the block's compact form, used when only that fits on the sheet. */
      compactHeight?: number
    }
  | {
      kind: 'rows'
      key: string
      headHeight: number
      footHeight: number
      rowHeights: number[]
      /** Keep at least one row on the sheet of the block that follows. */
      keepLastRowWithNext?: boolean
    }

export type PagePart =
  | {kind: 'block'; key: string; compact?: boolean}
  | {
      kind: 'rows'
      key: string
      /** First row index on this sheet. */
      from: number
      /** One past the last row index on this sheet. */
      to: number
    }

export type PaginationInput = {
  /** Body height of the first sheet (under the full header). */
  firstCapacity: number
  /** Body height of every later sheet (under the short header). */
  nextCapacity: number
  sections: FlowSection[]
}

export type PaginationResult = {
  pages: PagePart[][]
  /** Some part is taller than an empty sheet and will be cut unless the page shrinks. */
  overflow: boolean
}

// Sub-pixel rounding differs between the screen layout that is measured and the printed one.
const EPSILON = 0.5

export const paginate = ({
  firstCapacity,
  nextCapacity,
  sections,
}: PaginationInput): PaginationResult => {
  const pages: PagePart[][] = [[]]
  let used = 0
  let overflow = false
  const capacity = () => (pages.length === 1 ? firstCapacity : nextCapacity)
  const fits = (height: number) => used + height <= capacity() + EPSILON
  const newPage = () => {
    pages.push([])
    used = 0
  }
  const current = () => pages[pages.length - 1]

  sections.forEach((section, sectionIndex) => {
    if (section.kind === 'block') {
      if (
        !fits(section.height) &&
        current().length > 0 &&
        section.compactHeight !== undefined &&
        fits(section.compactHeight)
      ) {
        current().push({kind: 'block', key: section.key, compact: true})
        used += section.compactHeight
        return
      }
      if (!fits(section.height) && current().length > 0) newPage()
      if (section.height > capacity() + EPSILON) overflow = true
      current().push({kind: 'block', key: section.key})
      used += section.height
      return
    }

    const {headHeight, footHeight, rowHeights} = section
    if (rowHeights.length === 0) return
    const next = sections[sectionIndex + 1]
    const nextBlockHeight = section.keepLastRowWithNext && next?.kind === 'block' ? next.height : 0

    let from = 0
    while (from < rowHeights.length) {
      const frame = headHeight + footHeight
      if (!fits(frame + rowHeights[from]) && current().length > 0) newPage()

      const available = capacity() - used - frame + EPSILON
      let to = from
      let height = 0
      while (to < rowHeights.length && height + rowHeights[to] <= available) {
        height += rowHeights[to]
        to++
      }

      if (to === from) {
        // A row taller than an empty sheet: it is placed alone and reported.
        overflow = true
        height = rowHeights[from]
        to = from + 1
      } else if (
        to === rowHeights.length &&
        nextBlockHeight > 0 &&
        to - from > 1 &&
        used + frame + height + nextBlockHeight > capacity() + EPSILON &&
        frame + rowHeights[to - 1] + nextBlockHeight <= nextCapacity + EPSILON
      ) {
        // The block after the rows will not fit here: one row goes over with it.
        height -= rowHeights[to - 1]
        to--
      }

      current().push({kind: 'rows', key: section.key, from, to})
      used += frame + height
      from = to
      if (from < rowHeights.length) newPage()
    }
  })

  return {pages: pages.filter((page) => page.length > 0), overflow}
}

/** How many sheets a row section spans in a result. */
export const sheetsWithRows = (result: PaginationResult, key: string): number =>
  result.pages.filter((page) => page.some((part) => part.kind === 'rows' && part.key === key))
    .length
